import './ModalCumulativeAndDailyTable.scss';
import cn from 'classnames';
import { ReactElement, useMemo } from 'react';
import { BPMTable } from '@Components/StickyTable/BPMTable';
import { NumberFormatter } from '@Components/NumberFormatter/NumberFormatter';
import { Simplicity } from '../pacing-constants';
import { NON_MONETARY_GOALS, goalNameToKey, isPacingRatioGoalType, today } from '../pacingUtils';

// One row of the daily pacing table (values are formatted for display downstream).
interface DailyRow {
  date: string;
  simpleCleared: number;
  simpleBooked: number;
  remainingBudget: number;
  adjustedProjected: number;
}

interface ModalCumulativeAndDailyTableProps {
  data: Record<string, any>;
  goalName: string;
  start: string;
  end: string;
  simplicity: string;
}

export const ModalCumulativeAndDailyTable = ({
  data,
  goalName: propsGoalName,
  start,
  end,
  simplicity
}: ModalCumulativeAndDailyTableProps) => {
  const goalName = goalNameToKey(propsGoalName);
  const isAdjusted = simplicity === Simplicity.ADJUSTED;
  const isDerivedMetric = isPacingRatioGoalType(propsGoalName);
  const formatType = NON_MONETARY_GOALS.includes(propsGoalName) ? '#2' : '$';
  const formatDecimals = isDerivedMetric ? 2 : 0;

  const barActual = isAdjusted ? data.barAdjustedActual : data.barSimpleActual;
  const barIdeal = isAdjusted ? data.barAdjustedIdeal : data.barSimpleIdeal;

  const dataAdjusted = useMemo(() => {
    // A targetless (ratio-goalless) node omits booked/adjustedBooked and may omit the delivery maps —
    // guard like the sibling graph (ModalCumulativeAndDailyGraph) so the table degrades instead of
    // throwing when its Details modal opens on such a node.
    const initialRemaining = isAdjusted
      ? (data.adjustedBooked?.[goalName] ?? 0)
      : (data.booked?.[goalName] ?? 0);
    return Object.keys(data.simpleDeliveryByDate ?? {})
      .sort()
      .filter(
        (date: string) =>
          new Date(date) >= new Date(start) &&
          new Date(date) <= new Date(end) &&
          (!isDerivedMetric || new Date(date) < new Date(today))
      )
      .reduce(
        (acc: { rows: DailyRow[]; remaining: number }, date: string) => {
          const simpleCleared = data.simpleDeliveryByDate[date][goalName];
          const simpleBooked = barIdeal?.[date]?.[goalName] ?? 0;
          const adjustedProjected = barActual?.[date]?.[goalName] ?? 0;
          const remaining = acc.remaining - adjustedProjected;
          const [year, month, day] = date.split('-');
          return {
            rows: [
              ...acc.rows,
              {
                date: `${Number(month)}/${day}/${year.slice(-2)}`,
                simpleCleared,
                simpleBooked,
                remainingBudget: remaining,
                adjustedProjected
              }
            ],
            remaining
          };
        },
        { rows: [], remaining: initialRemaining }
      ).rows;
  }, [data, start, end, isDerivedMetric, goalName, barIdeal, barActual, isAdjusted]);

  const headers = useMemo(
    () => [
      {
        label: 'Date',
        name: 'date',
        width: 100,
        renderer: row => <div className='budget-date'>{row.date}</div>
      },
      {
        label: propsGoalName === 'Budget' ? 'Daily Delivery' : `Daily ${propsGoalName}`,
        name: 'simpleCleared',
        flex: 1,
        minFlexWidth: 150,
        renderer: row => (
          <NumberFormatter value={row.simpleCleared} type={formatType} decimals={formatDecimals} />
        )
      },
      ...(!isDerivedMetric
        ? [
            {
              label: propsGoalName === 'Budget' ? 'Expected Delivery' : `Expected ${propsGoalName}`,
              name: 'adjustedProjected',
              flex: 1,
              minFlexWidth: 150,
              renderer: row => (
                <NumberFormatter
                  value={row.adjustedProjected}
                  type={formatType}
                  decimals={formatDecimals}
                />
              )
            },
            {
              label: propsGoalName === 'Budget' ? 'Ideal Delivery' : `Ideal ${propsGoalName}`,
              name: 'simpleBooked',
              flex: 1,
              minFlexWidth: 150,
              renderer: row => (
                <NumberFormatter
                  value={row.simpleBooked}
                  type={formatType}
                  decimals={formatDecimals}
                />
              )
            },
            {
              label: propsGoalName === 'Budget' ? 'Remaining Budget' : 'Difference',
              name: 'remainingBudget',
              flex: 1,
              minFlexWidth: 150,
              renderer: row => (
                <NumberFormatter
                  value={row.remainingBudget}
                  type={formatType}
                  decimals={formatDecimals}
                />
              )
            }
          ]
        : [])
    ],
    [propsGoalName, isDerivedMetric, formatType, formatDecimals]
  );

  const bookedTotal = isAdjusted
    ? (data.adjustedBooked?.[goalName] ?? 0)
    : (data.booked?.[goalName] ?? 0);

  const totals = useMemo(() => {
    const computeTotal = (name: string) => {
      if (name === 'date') {
        return 'date';
      }
      if (name === 'simpleBooked') {
        return bookedTotal;
      }
      if (name === 'remainingBudget') {
        return bookedTotal - (data.simpleDeliveryTotal?.[goalName] ?? 0);
      }
      if (isDerivedMetric) {
        return data.simpleDeliveryTotal?.[goalName] ?? 0;
      }
      return dataAdjusted.map(row => row[name]).reduce((a, b) => a + b, 0);
    };
    return headers.reduce((acc, { name }) => ({ ...acc, [name]: computeTotal(name) }), {});
  }, [dataAdjusted, headers, isDerivedMetric, data, goalName, bookedTotal]);

  const totalsRenderer = ({ data: cellValue, style, classes }): ReactElement => (
    <div className={cn(...classes, 'total-container')} style={style}>
      {cellValue === 'date' ? (
        <span>
          TOTAL / AVG
          <br />
          for entire time period
        </span>
      ) : (
        <span>
          Total
          <br />
          <NumberFormatter value={cellValue} type={formatType} decimals={formatDecimals} />
        </span>
      )}
    </div>
  );

  return (
    <BPMTable
      className='pacing-cumulative-and-daily-table'
      data={dataAdjusted}
      headers={headers}
      filterBar={false}
      alternateColors={false}
      totals={totals}
      totalsRenderer={totalsRenderer}
    />
  );
};
